import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Briefcase, Phone, MapPin, Mail, Save, MessageCircle, Plus, ChevronDown, Tag, Check, Trash2, Navigation } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { searchSuppliers, createSupplier, deleteSupplier, getSupplierCategories, saveSupplierCategories } from "./api";
import { ffGradient } from "./FullFrameLogo";

const EMPTY_FORM = { name: "", phone: "", address: "", email: "", categories: [] };
const EMPTY_SLOTS = ["", "", "", "", "", ""];

const arrEq = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
};

/**
 * SuppliersModal — manage saved suppliers + 6 user-named categories.
 *
 * UX behaviour (per user spec):
 *  • Picking an existing supplier pre-fills the form AND immediately shows
 *    the WhatsApp + Email quick-action buttons (no save required).
 *  • Save button only appears for an UNSAVED form (new or edited fields).
 *    After a successful save → toast "הספק נרשם" + save button hides.
 *  • 6 category boxes at the top. Each box has its own tiny save button.
 *    Once a category is named, clicking the box assigns the currently-loaded
 *    supplier to that category (instantly persisted).
 *  • Suppliers in the dropdown show their category as a small badge.
 */
export function SuppliersModal({ open, onOpenChange, companyId }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedName, setSelectedName] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [originalForm, setOriginalForm] = useState(EMPTY_FORM);  // last saved snapshot
  const [saving, setSaving] = useState(false);
  // Categories: 6 named buckets the user defines manually.
  const [categories, setCategories] = useState(EMPTY_SLOTS);
  const [draftCategories, setDraftCategories] = useState(EMPTY_SLOTS);
  const [savingCatIdx, setSavingCatIdx] = useState(-1);
  // Filter: when set to a category name → dropdown shows only that bucket.
  const [filterCategory, setFilterCategory] = useState("");

  const loadSuppliers = async () => {
    if (!companyId) return;
    setLoadingList(true);
    try {
      const list = await searchSuppliers("", companyId);
      setSuppliers(list);
    } catch {
      toast.error("שגיאה בטעינת רשימת ספקים");
    } finally {
      setLoadingList(false);
    }
  };

  const loadCategories = async () => {
    if (!companyId) return;
    try {
      const slots = await getSupplierCategories(companyId);
      const padded = (slots.concat(EMPTY_SLOTS)).slice(0, 6);
      setCategories(padded);
      setDraftCategories(padded);
    } catch {
      // silent — keep empty slots
    }
  };

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setOriginalForm(EMPTY_FORM);
      setSelectedName("");
      setFilterCategory("");
      loadSuppliers();
      loadCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId]);

  const pickSupplier = (s) => {
    // Multi-category: prefer the array; fall back to legacy single value
    const cats = Array.isArray(s.categories) ? s.categories.filter(Boolean) : (s.category ? [s.category] : []);
    const next = {
      name: s.name || "",
      phone: s.phone || "",
      address: s.address || "",
      email: s.email || "",
      categories: cats,
    };
    setForm(next);
    setOriginalForm(next);
    setSelectedName(s.name || "");
    setShowDropdown(false);
  };

  const onChange = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const isDirty = useMemo(() => {
    if (!form.name.trim()) return false;
    if (["name", "phone", "address", "email"].some((k) => (form[k] || "") !== (originalForm[k] || ""))) {
      return true;
    }
    return !arrEq(form.categories || [], originalForm.categories || []);
  }, [form, originalForm]);

  const onSave = async (nextCategoriesOverride) => {
    if (!form.name.trim()) {
      toast.error("יש להזין שם ספק");
      return;
    }
    setSaving(true);
    const cats = nextCategoriesOverride !== undefined ? nextCategoriesOverride : (form.categories || []);
    const payload = { ...form, categories: cats };
    try {
      await createSupplier(payload, companyId);
      toast.success("הספק נרשם");
      setForm(payload);
      setOriginalForm(payload);
      setSelectedName(payload.name.trim());
      await loadSuppliers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בשמירת הספק");
    } finally {
      setSaving(false);
    }
  };

  const onDeleteSupplier = async () => {
    const n = (form.name || "").trim();
    if (!n) return;
    if (!window.confirm(`למחוק את הספק "${n}"? פעולה זו אינה הפיכה.`)) return;
    setSaving(true);
    try {
      await deleteSupplier(n, companyId);
      toast.success(`הספק "${n}" נמחק`);
      setForm(EMPTY_FORM);
      setOriginalForm(EMPTY_FORM);
      setSelectedName("");
      await loadSuppliers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה במחיקת הספק");
    } finally {
      setSaving(false);
    }
  };

  const onSaveCategory = async (idx) => {
    setSavingCatIdx(idx);
    try {
      const next = draftCategories.slice();
      next[idx] = (next[idx] || "").trim();
      const saved = await saveSupplierCategories(next, companyId);
      const padded = (saved.concat(EMPTY_SLOTS)).slice(0, 6);
      setCategories(padded);
      setDraftCategories(padded);
      toast.success(next[idx] ? `הקטגוריה "${next[idx]}" נשמרה` : "הקטגוריה רוקנה");
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בשמירת הקטגוריה");
    } finally {
      setSavingCatIdx(-1);
    }
  };

  const onCategoryChipClick = async (catName) => {
    if (!catName) return;  // unnamed slot — nothing to do
    if (!form.name.trim()) {
      // No supplier loaded → use as a filter only.
      setFilterCategory((cur) => (cur === catName ? "" : catName));
      return;
    }
    // Multi-category toggle: add if missing, remove if already assigned.
    const current = Array.isArray(form.categories) ? form.categories : [];
    const next = current.includes(catName)
      ? current.filter((c) => c !== catName)
      : [...current, catName];
    setForm((prev) => ({ ...prev, categories: next }));
    await onSave(next);
  };

  const cleanPhone = useMemo(() => (form.phone || "").replace(/[^\d+]/g, ""), [form.phone]);
  const waUrl = cleanPhone ? `https://wa.me/${cleanPhone.replace(/^\+/, "")}` : "";
  const mailUrl = form.email ? `mailto:${form.email.trim()}` : "";
  const navUrl = form.address
    ? `https://waze.com/ul?q=${encodeURIComponent(form.address.trim())}&navigate=yes`
    : "";
  const hasLoadedSupplier = !!form.name.trim();
  const showContactActions = hasLoadedSupplier && (cleanPhone || form.email || form.address);
  const isExistingSupplier = hasLoadedSupplier && !!originalForm.name.trim();

  const visibleSuppliers = filterCategory
    ? suppliers.filter((s) => {
        const cats = Array.isArray(s.categories) ? s.categories : (s.category ? [s.category] : []);
        return cats.includes(filterCategory);
      })
    : suppliers;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-stone-800 text-stone-100 border-white/10 max-h-[92vh] overflow-y-auto"
        dir="rtl"
        data-testid="suppliers-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Briefcase className="h-5 w-5 text-pink-400" />
            ניהול ספקים
          </DialogTitle>
          <DialogDescription className="text-stone-400">
            צור קטגוריות, שמור ספקים, ושייך כל ספק לקטגוריה בלחיצה אחת.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* 6 category boxes — user names each one manually, small save next
              to each. Once named, clicking a box either assigns the currently
              loaded supplier to that category OR filters the dropdown. */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-stone-300 text-xs font-bold flex items-center gap-1.5">
                <Tag className="h-3.5 w-3.5 text-pink-400" />
                קטגוריות (6)
              </Label>
              {filterCategory && (
                <button
                  type="button"
                  onClick={() => setFilterCategory("")}
                  className="text-[11px] text-pink-300 hover:text-pink-200 font-bold"
                  data-testid="suppliers-clear-filter"
                >
                  נקה סינון
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Array.from({ length: 6 }).map((_, idx) => {
                const savedName = categories[idx] || "";
                const draftName = draftCategories[idx] || "";
                const isAssigned = savedName && Array.isArray(form.categories) && form.categories.includes(savedName);
                const isFilter = savedName && filterCategory === savedName;
                return (
                  <div
                    key={idx}
                    className={`group relative rounded-xl border p-2 transition ${
                      isAssigned
                        ? "border-pink-500/60 bg-pink-500/15"
                        : isFilter
                        ? "border-sky-500/60 bg-sky-500/10"
                        : "border-white/10 bg-stone-700"
                    }`}
                    data-testid={`supplier-category-box-${idx}`}
                  >
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={draftName}
                        onChange={(e) => {
                          const next = draftCategories.slice();
                          next[idx] = e.target.value;
                          setDraftCategories(next);
                        }}
                        placeholder="שם קטגוריה"
                        className="flex-1 bg-transparent text-sm text-stone-100 placeholder:text-stone-500 focus:outline-none min-w-0"
                        data-testid={`supplier-category-input-${idx}`}
                      />
                      {draftName !== savedName && (
                        <button
                          type="button"
                          onClick={() => onSaveCategory(idx)}
                          disabled={savingCatIdx === idx}
                          className="shrink-0 p-1 rounded hover:bg-white/10 text-pink-400 disabled:opacity-50"
                          title="שמור קטגוריה"
                          data-testid={`supplier-category-save-${idx}`}
                        >
                          <Save className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    {savedName && (
                      <button
                        type="button"
                        onClick={() => onCategoryChipClick(savedName)}
                        disabled={saving}
                        className="mt-1 w-full text-[11px] font-bold text-pink-300 hover:text-pink-200 flex items-center justify-center gap-1 disabled:opacity-50"
                        data-testid={`supplier-category-apply-${idx}`}
                      >
                        {isAssigned ? <Check className="h-3 w-3" /> : null}
                        {hasLoadedSupplier
                          ? (isAssigned ? "הסר שיוך" : "שייך ספק")
                          : (isFilter ? "מסונן" : "סנן")}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Existing suppliers dropdown */}
          <div className="relative">
            <Label className="text-stone-300 text-xs font-bold">ספק קיים</Label>
            <button
              type="button"
              onClick={() => setShowDropdown((v) => !v)}
              className="mt-1 w-full flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-stone-700 px-3 py-2 text-sm hover:bg-stone-700/80 transition"
              data-testid="suppliers-dropdown-toggle"
            >
              <span className={selectedName ? "text-stone-100" : "text-stone-500"}>
                {selectedName ||
                  (loadingList
                    ? "טוען..."
                    : visibleSuppliers.length === 0
                    ? filterCategory
                      ? `אין ספקים בקטגוריה "${filterCategory}"`
                      : "אין ספקים שמורים — צור חדש"
                    : "בחר מהרשימה...")}
              </span>
              <ChevronDown className={`h-4 w-4 text-stone-400 transition ${showDropdown ? "rotate-180" : ""}`} />
            </button>

            {showDropdown && visibleSuppliers.length > 0 && (
              <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-white/10 bg-stone-800 shadow-xl" data-testid="suppliers-dropdown-list">
                {visibleSuppliers.map((s, idx) => {
                  const sCats = Array.isArray(s.categories) ? s.categories : (s.category ? [s.category] : []);
                  return (
                  <button
                    key={`${s.name}-${idx}`}
                    type="button"
                    onClick={() => pickSupplier(s)}
                    className="w-full text-right px-3 py-2 text-sm hover:bg-pink-600/20 transition border-b border-white/5 last:border-0"
                    data-testid={`suppliers-option-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-bold text-stone-100">{s.name}</span>
                      {sCats.length > 0 && (
                        <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                          {sCats.map((c, ci) => (
                            <span key={ci} className="inline-flex items-center gap-0.5 text-[10px] font-bold text-pink-300 bg-pink-500/15 border border-pink-500/30 px-1.5 py-0.5 rounded-full">
                              <Tag className="h-2.5 w-2.5" />
                              {c}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {(s.phone || s.email) && (
                      <div className="text-[11px] text-stone-400 mt-0.5">
                        {s.phone && <span>📞 {s.phone}</span>}
                        {s.phone && s.email && <span> · </span>}
                        {s.email && <span>✉ {s.email}</span>}
                      </div>
                    )}
                  </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* New supplier button (clears form) */}
          <button
            type="button"
            onClick={() => {
              setForm(EMPTY_FORM);
              setOriginalForm(EMPTY_FORM);
              setSelectedName("");
              setShowDropdown(false);
            }}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-pink-300 hover:text-pink-200 transition"
            data-testid="suppliers-new-btn"
          >
            <Plus className="h-3.5 w-3.5" />
            ספק חדש
          </button>

          {/* Form */}
          <div className="space-y-3 pt-2 border-t border-white/10">
            <div>
              <Label className="text-stone-300 text-xs font-bold flex items-center gap-1.5">
                <Briefcase className="h-3.5 w-3.5 text-pink-400" />
                שם ספק *
              </Label>
              <Input
                value={form.name}
                onChange={(e) => onChange("name", e.target.value)}
                placeholder="למשל: סטודיו אורות"
                className="mt-1 bg-stone-700 border-white/10 text-stone-100 placeholder:text-stone-500"
                data-testid="supplier-name-input"
              />
            </div>
            <div>
              <Label className="text-stone-300 text-xs font-bold flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-pink-400" />
                טלפון
              </Label>
              <Input
                value={form.phone}
                onChange={(e) => onChange("phone", e.target.value)}
                placeholder="050-1234567"
                dir="ltr"
                className="mt-1 bg-stone-700 border-white/10 text-stone-100 placeholder:text-stone-500 text-right"
                data-testid="supplier-phone-input"
              />
            </div>
            <div>
              <Label className="text-stone-300 text-xs font-bold flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-pink-400" />
                כתובת
              </Label>
              <Input
                value={form.address}
                onChange={(e) => onChange("address", e.target.value)}
                placeholder="רחוב, מספר, עיר"
                className="mt-1 bg-stone-700 border-white/10 text-stone-100 placeholder:text-stone-500"
                data-testid="supplier-address-input"
              />
            </div>
            <div>
              <Label className="text-stone-300 text-xs font-bold flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 text-pink-400" />
                אימייל
              </Label>
              <Input
                value={form.email}
                onChange={(e) => onChange("email", e.target.value)}
                placeholder="supplier@example.com"
                dir="ltr"
                type="email"
                className="mt-1 bg-stone-700 border-white/10 text-stone-100 placeholder:text-stone-500 text-right"
                data-testid="supplier-email-input"
              />
            </div>
            {Array.isArray(form.categories) && form.categories.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="supplier-current-categories">
                <span className="text-[11px] text-stone-400 font-bold">שיוכים:</span>
                {form.categories.map((c, ci) => (
                  <span key={ci} className="inline-flex items-center gap-1 text-[11px] font-bold text-pink-300 bg-pink-500/15 border border-pink-500/30 px-2 py-0.5 rounded-full">
                    <Tag className="h-3 w-3" />
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Actions: save only while dirty; contact buttons whenever supplier loaded. */}
          <div className="flex flex-col gap-2 pt-3 border-t border-white/10">
            {isDirty && (
              <Button
                type="button"
                onClick={() => onSave()}
                disabled={saving || !form.name.trim()}
                className={`${ffGradient} text-white font-bold disabled:opacity-50`}
                data-testid="supplier-save-btn"
              >
                <Save className="h-4 w-4 ml-1.5" />
                {saving ? "שומר..." : "שמור ספק"}
              </Button>
            )}

            {showContactActions && (
              <div className="grid grid-cols-3 gap-2" data-testid="supplier-contact-actions">
                <a
                  href={waUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => { if (!waUrl) { e.preventDefault(); toast.error("אין טלפון שמור"); } }}
                  className={`inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-bold transition ${waUrl ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "bg-stone-700 text-stone-500 cursor-not-allowed"}`}
                  data-testid="supplier-wa-btn"
                >
                  <MessageCircle className="h-4 w-4" />
                  וואטסאפ
                </a>
                <a
                  href={navUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => { if (!navUrl) { e.preventDefault(); toast.error("אין כתובת שמורה"); } }}
                  className={`inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-bold transition ${navUrl ? "bg-amber-600 hover:bg-amber-500 text-white" : "bg-stone-700 text-stone-500 cursor-not-allowed"}`}
                  data-testid="supplier-nav-btn"
                >
                  <Navigation className="h-4 w-4" />
                  ניווט
                </a>
                <a
                  href={mailUrl || "#"}
                  onClick={(e) => { if (!mailUrl) { e.preventDefault(); toast.error("אין אימייל שמור"); } }}
                  className={`inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-bold transition ${mailUrl ? "bg-sky-600 hover:bg-sky-500 text-white" : "bg-stone-700 text-stone-500 cursor-not-allowed"}`}
                  data-testid="supplier-email-btn"
                >
                  <Mail className="h-4 w-4" />
                  אימייל
                </a>
              </div>
            )}

            {isExistingSupplier && (
              <button
                type="button"
                onClick={onDeleteSupplier}
                disabled={saving}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-300 transition disabled:opacity-50"
                data-testid="supplier-delete-btn"
              >
                <Trash2 className="h-3.5 w-3.5" />
                מחק ספק
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
